import { Formik, FormikActions, FormikProps } from 'formik';
import React, { PureComponent, ReactNode } from 'react';
import * as yup from 'yup';
import { FormContact } from '../../bzit';
import { Button } from '../../components/Button';
import { Form, Textarea } from '../../components/Form';
import { userCountry, userLanguage } from '../../configs/env';
import { reportError } from '../../error-handling/reportError';
import {
  withLang,
  WithLangProps
} from '../../provider/LanguageProvider/withLang';
import Typo3Label from '../../provider/Typo3Provider/Typo3Label';
import {
  withTypo3,
  WithTypo3Props
} from '../../provider/Typo3Provider/withTypo3';
import DataProtection, {
  DEFAULT_CODE_LOCATION
} from './components/DataProtection';
import { FormInput } from './components/FormInput';
import GenderSelect from './components/GenderSelect';
import RequiredInfo from './RequiredInfo';

interface ContactFormProps<Extra extends object> {
  onSubmit: (
    formData: FormContact & Extra,
    formActions: FormikActions<FormContact & Extra>
  ) => Promise<void>;
  initialExtraValues: Extra;
  extraValidationSchema?: any;
  renderBefore?: (props: FormikProps<FormContact & Extra>) => ReactNode;
  renderAfter?: (props: FormikProps<FormContact & Extra>) => ReactNode;
  skipSubmitButton?: boolean;
  skipMessage?: boolean;
  codeLocation?: string | ((formData: FormContact & Extra) => string);
  extraRequireds?: (keyof FormContact)[];
}

interface ContactFormState {}

class ContactForm<Extra extends object> extends PureComponent<
  ContactFormProps<Extra> & WithTypo3Props & WithLangProps,
  ContactFormState
> {
  public state: ContactFormState = {};

  // TODO: how to merge FormContact with Extra?
  public initialFormValues: FormContact & Extra = {
    codeSalutation: 'M',
    name: '',
    email: '',
    phone: '',
    message: '',
    dataprotection: false,
    // TODO: make sure, where these infos come from (via `navigator`-object or via URL-Content)
    locale: `${userCountry}:${userLanguage}`,
    ...(this.props.initialExtraValues || {})
  };

  public onSubmit = async (
    formData: FormContact & Extra,
    formActions: FormikActions<FormContact & Extra>
  ) => {
    const {
      onSubmit,
      lang: { countryCode, languageCode }
    } = this.props;
    const { setSubmitting } = formActions;
    try {
      await onSubmit(
        { ...formData, locale: `${countryCode}:${languageCode}` },
        formActions
      );
      setSubmitting(false);
    } catch (e) {
      reportError('ContactForm.onSubmit()', e);
    } finally {
      setSubmitting(false);
    }
  };

  public getValidationSchema = () => {
    const { extraValidationSchema, extraRequireds = [], skipMessage } = this.props;
    const codeSalutation = yup.mixed().oneOf(['M', 'F']);
    const name = yup.string();
    const email = yup
      .string()
      .email()
      .required();
    const phone = yup.string();
    const dataprotection = yup
      .boolean()
      .oneOf([true])
      .required();
    const locale = yup.string().required();
    return yup.object().shape({
      codeSalutation: extraRequireds.includes('codeSalutation')
        ? codeSalutation.required()
        : codeSalutation,
      name: extraRequireds.includes('name') ? name.required() : name,
      email,
      phone: extraRequireds.includes('phone') ? phone.required() : phone,
      dataprotection,
      locale,
      ...(!skipMessage && this.getMessageValidationSchema(),
        typeof extraValidationSchema === 'function'
        ? extraValidationSchema()
        : extraValidationSchema)
    });
  };

  private getMessageValidationSchema = () => {
    const message = yup.string().required();
    return  {message};
  }

  // #region renderForm
  public renderForm = (formikProps: FormikProps<FormContact & Extra>) => {
    const {
      typo3,
      renderBefore,
      renderAfter,
      skipSubmitButton = false,
      skipMessage = false,
      codeLocation = DEFAULT_CODE_LOCATION,
      extraRequireds = []
    } = this.props;
    const {
      handleSubmit,
      values,
      errors,
      touched,
      handleChange,
      handleBlur,
      isSubmitting
    } = formikProps;
    return (
      <Form onSubmit={handleSubmit}>
        {renderBefore && renderBefore(formikProps)}
        <GenderSelect
          value={values.codeSalutation}
          error={!!errors.codeSalutation && !!touched.codeSalutation}
          handleChange={handleChange}
          handleBlur={handleBlur}
        />

        <FormInput
          label={<Typo3Label label={'form.label.name'} />}
          type="text"
          name="name"
          placeholder={typo3.translateTypo3(
            'form.placeholder.name',
            null,
            'z.B. Max Mustermann'
          )}
          value={values.name}
          error={!!errors.name && !!touched.name}
          onChange={handleChange}
          onBlur={handleBlur}
          required={extraRequireds.includes('name')}
        />

        <FormInput
          label={<Typo3Label label={'form.label.email'} />}
          type="email"
          name="email"
          placeholder={typo3.translateTypo3(
            'form.placeholder.email',
            null,
            'z.B. maxmuster@googlemail.com'
          )}
          value={values.email}
          error={!!errors.email && !!touched.email}
          onChange={handleChange}
          onBlur={handleBlur}
          required
        />

        <FormInput
          label={<Typo3Label label={'form.label.phone'} />}
          type="text"
          name="phone"
          placeholder={typo3.translateTypo3(
            'form.placeholder.phone',
            null,
            'z.B. +49 123 456789'
          )}
          value={values.phone}
          error={!!errors.phone && !!touched.phone}
          onChange={handleChange}
          onBlur={handleBlur}
          required={extraRequireds.includes('phone')}
        />

        {!skipMessage && (
          <FormInput
            label={<Typo3Label label={'form.label.message'} />}
            component={Textarea}
            name="message"
            value={values.message}
            error={!!errors.message && !!touched.message}
            onChange={handleChange}
            onBlur={handleBlur}
            required
          />
        )}

        <DataProtection
          value={values.dataprotection}
          error={!!errors.dataprotection && !!touched.dataprotection}
          handleChange={handleChange}
          handleBlur={handleBlur}
          codeLocation={
            typeof codeLocation === 'function'
              ? codeLocation(values)
              : codeLocation
          }
        />

        <RequiredInfo />

        {renderAfter && renderAfter(formikProps)}

        {!skipSubmitButton && (
          <Button primary large disabled={isSubmitting}>
            <Typo3Label label={'form.button.send'} />
          </Button>
        )}
      </Form>
    );
  };
  // #endregion

  public render() {
    return (
      <Formik
        initialValues={this.initialFormValues}
        onSubmit={this.onSubmit}
        render={this.renderForm}
        validationSchema={this.getValidationSchema}
      />
    );
  }
}

// typescript hack to have a generic type in `ContactFormProps`
// across the whole higher order component pipeline

// here we remove the specific generic type by using `any`
const HOC = withTypo3<ContactFormProps<any>>(
  withLang<ContactFormProps<any> & WithTypo3Props>(ContactForm)
);

// here we add the specific generic type again
const ContactFormExport = <Extra extends object>(
  props: ContactFormProps<Extra>
) => {
  return <HOC {...props} />;
};

ContactFormExport.displayName = 'ContactForm';

export default ContactFormExport;
